import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CheckCircle2, Loader2, Mail, MessageCircle, Send } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { LINKS } from "@/lib/site";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = { full_name: "", business_name: "", email: "", phone: "", message: "", website: "" };

const ContactForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (field) => (e) => setForm((f) => ({ ...f, [field]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, {
        full_name: form.full_name,
        business_name: form.business_name || null,
        email: form.email,
        phone: form.phone || null,
        message: form.message,
        website: form.website || null,
      });
      toast.success("Your message has been received.");
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error("Something went wrong sending your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const fieldClass =
    "h-auto rounded-xl border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus-visible:ring-1 focus-visible:ring-whatsapp/60 focus-visible:border-whatsapp/60";

  return (
    <section
      id="contact"
      data-testid="contact-section"
      className="border-t border-white/5 bg-ink py-14 lg:py-16"
    >
      <div className="mx-auto grid max-w-7xl gap-10 px-5 lg:grid-cols-[1fr_1.25fr] lg:gap-14 lg:px-10">
        <Reveal>
          <SectionLabel>Contact</SectionLabel>
          <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white md:text-4xl">
            Contact Jewish Global Wire
          </h2>
          <div className="mt-5 h-px w-16 bg-gradient-to-r from-silver/60 to-transparent" />
          <p className="mt-5 max-w-md text-base leading-relaxed text-silver">
            Send us a message regarding advertising or general business inquiries.
          </p>
          <div className="mt-8 space-y-4">
            <a
              href={LINKS.phoneHref}
              data-testid="contact-phone-link"
              className="group flex items-center gap-4"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-whatsapp transition-colors duration-200 group-hover:bg-whatsapp group-hover:text-white">
                <MessageCircle size={18} strokeWidth={2} />
              </span>
              <span>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-silver-dark">
                  WhatsApp
                </span>
                <span className="block text-[15px] font-bold text-white transition-colors duration-200 group-hover:text-whatsapp">
                  {LINKS.phoneDisplay}
                </span>
              </span>
            </a>
            <a
              href={LINKS.email}
              data-testid="contact-email-link"
              className="group flex items-center gap-4"
            >
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/5 text-silver-light transition-colors duration-200 group-hover:bg-white group-hover:text-black">
                <Mail size={18} strokeWidth={2} />
              </span>
              <span>
                <span className="block text-[11px] font-semibold uppercase tracking-[0.15em] text-silver-dark">
                  Email
                </span>
                <span className="block break-all text-[15px] font-bold text-white transition-colors duration-200 group-hover:text-silver-light">
                  {LINKS.emailAddress}
                </span>
              </span>
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="rounded-2xl border border-white/10 bg-white/[0.04] p-6 sm:p-8">
            {sent ? (
              <div data-testid="contact-success-message" className="py-8 text-center">
                <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-whatsapp/12 text-whatsapp">
                  <CheckCircle2 size={30} strokeWidth={1.8} />
                </span>
                <h3 className="mt-5 font-display text-2xl font-bold text-white">
                  Message received.
                </h3>
                <p className="mx-auto mt-3 max-w-md text-[15px] leading-relaxed text-silver">
                  Thank you for contacting Jewish Global Wire. Our team has received
                  your message and will be in touch shortly.
                </p>
                <button
                  type="button"
                  data-testid="contact-send-another-button"
                  onClick={() => setSent(false)}
                  className="mt-7 inline-flex items-center justify-center rounded-xl border border-white/25 px-6 py-3 text-sm font-bold text-white transition-colors duration-200 hover:bg-white hover:text-black"
                >
                  Send another message
                </button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} data-testid="contact-form">
                <input
                  type="text"
                  name="website"
                  value={form.website}
                  onChange={update("website")}
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                  className="pointer-events-none absolute -left-[9999px] h-0 w-0 opacity-0"
                />
                <div className="grid gap-4 sm:grid-cols-2">
                  <div className="space-y-1.5">
                    <Label htmlFor="full_name" className="text-sm font-semibold text-silver-light">
                      Full name
                    </Label>
                    <Input
                      id="full_name"
                      data-testid="contact-full-name-input"
                      required
                      value={form.full_name}
                      onChange={update("full_name")}
                      placeholder="Your full name"
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="business_name" className="text-sm font-semibold text-silver-light">
                      Business name
                    </Label>
                    <Input
                      id="business_name"
                      data-testid="contact-business-name-input"
                      value={form.business_name}
                      onChange={update("business_name")}
                      placeholder="Your business or organization"
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="email" className="text-sm font-semibold text-silver-light">
                      Email address
                    </Label>
                    <Input
                      id="email"
                      type="email"
                      data-testid="contact-email-input"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="you@example.com"
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="phone" className="text-sm font-semibold text-silver-light">
                      Phone number
                    </Label>
                    <Input
                      id="phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="+1 (___) ___-____"
                      className={fieldClass}
                    />
                  </div>
                  <div className="space-y-1.5 sm:col-span-2">
                    <Label htmlFor="message" className="text-sm font-semibold text-silver-light">
                      Message
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message-input"
                      required
                      rows={4}
                      value={form.message}
                      onChange={update("message")}
                      placeholder="Tell us about your inquiry"
                      className="rounded-xl border-white/15 bg-white/[0.06] px-4 py-3 text-[15px] text-white placeholder:text-slate-500 focus-visible:ring-1 focus-visible:ring-whatsapp/60 focus-visible:border-whatsapp/60"
                    />
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={sending}
                  data-testid="contact-submit-button"
                  className="mt-6 inline-flex w-full items-center justify-center gap-2.5 rounded-xl bg-white px-7 py-4 text-sm font-bold text-black transition-all duration-200 hover:-translate-y-0.5 hover:bg-silver-light disabled:opacity-60"
                >
                  {sending ? (
                    <>
                      <Loader2 size={17} className="animate-spin" />
                      Sending…
                    </>
                  ) : (
                    <>
                      <Send size={16} />
                      Submit
                    </>
                  )}
                </button>
              </form>
            )}
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default ContactForm;
